import {
  animate,
  AnimationBuilder,
  AnimationPlayer,
  style,
} from '@angular/animations';
import {
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef, 
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { MainPanelActions } from '../../actions/app/main-panel.actions';
import {
  ResetThemeAll,
  UpdateColorClassesFooter,
  UpdateColorClassesNavbar,
  UpdateColorClassesToolbar,
  UpdateCustomScrollbars,
  UpdateLayoutFooter,
  UpdateLayoutMode,
  UpdateLayoutTheme,
  UpdateLayoutToolbar,
  UpdateRouterAnimation,
} from '../../actions/app/theme.action';
import { AppAnimations } from '../../animations';
import { AllThemes } from '../../configs/theme.config';
import {
  NavigationLayoutSettingStore,
  NavigationType,
  UpdateNavigationPosition,
} from '../../stores/app/navbar.state';
import { AppThemeStore, IMainPanelState } from '../../stores/app/theme.state';
import { NkPipesModule } from '../../pipes/pipes.module';
// import { NkMaterialColorPickerModule } from '../material-color-picker/material-color-picker.module';
import {MaterialColorPickerComponent } from '../material-color-picker/material-color-picker.component'

@Component({
  selector: 'app-theme-options',
  templateUrl: './theme-options.component.html',
  styleUrls: ['./theme-options.component.scss'],
  animations: AppAnimations,
  standalone: true,
  imports: [
    CommonModule,
    FlexLayoutModule,
    FormsModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatOptionModule,
    MatRadioModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    // NkMaterialColorPickerModule,
    NkPipesModule,
    MaterialColorPickerComponent
  ]
})
export class NkThemeOptionsComponent implements OnInit, OnDestroy {
  @Input() navigation: any;
  @ViewChild('openButton', { static: true })
  openButton!: ElementRef;

  @ViewChild('panel', { static: true })
  panel!: ElementRef;

  @ViewChild('overlay', { static: true })
  overlay!: ElementRef;

  public player!: AnimationPlayer;
  public config: any;
  public themes = AllThemes;

  onConfigChanged!: Subscription;

  @HostBinding('class.bar-closed')
  barClosed: boolean;

  appThemeConfig$: Observable<any> = inject(Store).select(AppThemeStore.appThemeConfig);

  navLayoutConfig$: Observable<any> = inject(Store).select(NavigationLayoutSettingStore.navLayoutConfig);

  mainPanelState$: Observable<any> = inject(Store).select(AppThemeStore.MainPanelState);
  
  mainPanelStateSubscriber!: Subscription;

  constructor(
    private readonly animationBuilder: AnimationBuilder,
    private readonly store: Store
  ) {
    this.barClosed = true;
  }

  ngOnInit() {
    this.mainPanelStateSubscriber = this.mainPanelState$
      .pipe(
        map((v: IMainPanelState) => v.themeOptionPanelOpen),
        distinctUntilChanged()
      )
      .subscribe((v) => (v ? this.showBar() : this.hideBar()));
  }

  ngOnDestroy() {
    this.mainPanelStateSubscriber.unsubscribe();
  }
  closeBar() {
    this.store.dispatch(new MainPanelActions.ThemeOptionPanelOpen(false));
  }

  openBar() {
    this.store.dispatch(new MainPanelActions.ThemeOptionPanelOpen(true));
  }
  displayPercentage = (value: number): string => `${value}%`;

  
  hideBar() {
    this.player = this.animationBuilder
      .build([
        style({ transform: 'translate3d(0,0,0)' }),
        animate(
          '400ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translate3d(100%,0,0)' })
        ),
      ])
      .create(this.panel.nativeElement);

    this.player.play();

    this.player.onDone(() => (this.barClosed = true));
  }

  showBar() {
    this.barClosed = false;

    this.player = this.animationBuilder
      .build([
        style({ transform: 'translate3d(100%,0,0)' }),
        animate(
          '400ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translate3d(0,0,0)' })
        ),
      ])
      .create(this.panel.nativeElement);

    this.player.play();
  }

  reset() {
    document.body.className = '';
    this.store.dispatch(new ResetThemeAll());
  }

  updateColorClassesFooter(klass: string) {
    this.store.dispatch(new UpdateColorClassesFooter(klass));
  }

  updateColorClassesNavbar(klass: string) {
    this.store.dispatch(new UpdateColorClassesNavbar(klass));
  }

  updateColorClassesToolbar(klass: string) {
    this.store.dispatch(new UpdateColorClassesToolbar(klass));
  }

  updateCustomScrollbars(data: any) {
    this.store.dispatch(new UpdateCustomScrollbars(data));
  }

  updateLayoutFooter(position: 'below' | 'above' | 'none') {
    this.store.dispatch(new UpdateLayoutFooter(position));
  }

  updateLayoutMode(type: 'boxed' | 'fullwidth') {
    this.store.dispatch(new UpdateLayoutMode(type));
  }

  updateLayoutNavigation(position: NavigationType) {
    this.store.dispatch(new UpdateNavigationPosition(position));
  }

currentThemeClass: string | null = null;

updateLayoutTheme(theme: keyof typeof AllThemes) {
  
  const body = document.body; 
  const newClass = AllThemes[theme]?.klass;
   if (this.currentThemeClass) {
    body.classList.remove(this.currentThemeClass);
  }

  if (newClass) {
    body.classList.add(newClass);
    this.currentThemeClass = newClass;
  }

  this.store.dispatch(new UpdateLayoutTheme(theme));
}



  updateLayoutToolbar(position: 'above' | 'below' | 'none') {
    this.store.dispatch(new UpdateLayoutToolbar(position));
  }

  updateRouterAnimation(data: any) {
    this.store.dispatch(new UpdateRouterAnimation(data));
  }
}